import React, { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Loader from '@justgiving/loading/es/Loading';
import { useTracking } from '@justgiving/react-tracking';

import classNames from 'classnames';

import { PageStatus } from '../../../types/__generated__';
import { PageMetaContext } from '../../context/PageMetaContext';
import useBasePageQuery from '../../hooks/useBasePageQuery';
import { useNotNullableContext } from '../../hooks/useNotNullableContext';
import isLightColor from '../../utils/colors/isLightColor';

import styles from './DonateButton.scss';

type DonateButtonProps = {
  className?: string;
  inactivePageBehaviour?: 'disable' | 'hide';
  onClick?: () => void;
  trackingDataPageSection?: string;
};

export const DonateButton: FunctionComponent<DonateButtonProps> = ({
  className,
  inactivePageBehaviour = 'disable',
  onClick,
  trackingDataPageSection,
}) => {
  const basePageData = useBasePageQuery();
  const { currentUrl, pageType } = useNotNullableContext(PageMetaContext);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation('common', { useSuspense: false });
  const track = useTracking();

  if (!basePageData.data?.page) {
    return null;
  }

  const { status, relationships, branding, lifecycle } = basePageData.data.page;
  //  lifecycle is a PageStatus equivalent for PS pages
  //  it contains an array with alowed actions that should drive the front end.
  const isDonateActive =
    status === PageStatus.Active ||
    (lifecycle?.allowedActions?.indexOf('public_donate') !== -1 &&
      lifecycle?.allowedActions?.indexOf('public_donate') !== undefined);

  const isMultiBeneficiaryCampaign =
    relationships && relationships.beneficiaries.totalCount > 1;

  if (inactivePageBehaviour === 'hide' && !isDonateActive) {
    return null;
  }

  const buttonType =
    branding?.primaryColor && isLightColor(branding.primaryColor)
      ? 'jg-btn--ghost'
      : 'jg-btn--themed';

  function handleClick() {
    setLoading(true);

    track({
      page_section: `${pageType} page > ${trackingDataPageSection ||
        'unknown'}`.toLowerCase(),
      event: 'link click',
      action_name: 'link click',
      event_value: 'donate',
      subtype: 'donate',
    });

    if (onClick) {
      onClick();
    }
  }

  return !isMultiBeneficiaryCampaign ? (
    <a
      type="button"
      className={classNames('jg-btn', buttonType, styles.button, className, {
        [styles.disabled]: !isDonateActive,
      })}
      onClick={handleClick}
      href={`${currentUrl}/donate`}
    >
      {loading ? (
        <Loader size="small" colour="white" />
      ) : (
        t('common.donateLink', 'Donate')
      )}
    </a>
  ) : null;
};

export default DonateButton;
